import moment from "moment";
import momentTz from "moment-timezone";
import { toast } from "react-toastify";

export const showToast = (type, message) => {
  const toastOptions = {
    position: "bottom-left",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    theme: "dark",
  };

  if (type === "success") {
    toast.success(message, toastOptions);
  } else {
    toast.error(message, toastOptions);
  }
};

export const isValidEmail = (email) => {
  var pattern = new RegExp(
    /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
  );
  return pattern.test(email);
};

export const isValidPhone = (phoneNumber) => {
  const phoneRegex =
    /^(?:\+?\d{1,3})?[-.\s]?(\(?\d{1,4}\)?[-.\s]?)?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,4}$/;
  return phoneRegex.test(phoneNumber);
};

export const getDistance = (location1, location2, disIn = "miles") => {
  const loc1Latitude = location1.coordinates[1];
  const loc1Longitude = location1.coordinates[0];

  const R = disIn === "miles" ? 3958.8 : 6371; // Radius of the earth in km

  const dLat = deg2rad(location2.latitude - loc1Latitude);
  const dLon = deg2rad(location2.longitude - loc1Longitude);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(loc1Latitude)) *
      Math.cos(deg2rad(location2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km

  return (distance + Number.EPSILON).toFixed(2);
};

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

export const appendZero = (number) => {
  return String(number).padStart(2, "0");
};

export const getFilterTime = (remainingTime) => {
  const hours = Math.floor(remainingTime / 3600);
  const minutes = Math.floor((remainingTime % 3600) / 60);
  const seconds = remainingTime % 60;

  return `${appendZero(hours)}:${appendZero(minutes)}:${appendZero(seconds)}`;
};

export const convertTimeTo12h = (time) => {
  return moment(time, ["hh:mmA", "hh:mm A"]).format("hh:mm A");
};

export const convertTimeTo24h = (time) => {
  return moment(time, ["hh:mmA", "hh:mm A"]).format("HH:mm");
};

export const calcultePrice = (ratePerHour, block) => {
  let minutes = 0;

  if (block.includes("minutes")) {
    minutes = parseInt(block); // "15 mins" → 15
  } else if (block.includes("hour")) {
    const hours = parseFloat(block); // "2h", "1 h" → 2, 1
    minutes = hours * 60;
  } else if (block.includes("day")) {
    const days = parseFloat(block); // "2 days", "1 day" → 2, 1
    minutes = days * 24;
  }

  let price = 0;

  if (block.includes("day")) {
    price = minutes * ratePerHour;
  } else {
    price = (minutes / 60) * ratePerHour;
  }

  return price.toFixed(2);
};

// Flat-rate spots charge one fixed amount regardless of duration. Backend
// stores the flag as boolean, string, or 1/0 depending on the path, so
// coerce defensively.
export const isFlatRateSpot = (spot) => {
  const v = spot && spot.isFlatRate;
  return v === true || v === "true" || v === 1 || v === "1";
};

// "$50" for whole dollars, "$3.50" for fractional. Stops "$NaN" leaking
// into the UI when a fee field is missing.
export const formatMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

// "$50" for flat-rate, "$3/h" for hourly. Use on cards/lists/tables.
export const formatSpotPrice = (spot) => {
  if (!spot) return "";
  if (isFlatRateSpot(spot)) return `$${formatMoney(spot.flatRate)}`;
  return `$${formatMoney(spot.ratePerHour)}/h`;
};

const BOOKING_STATUS_LABELS = {
  "payment-pending": "Payment Pending",
  "request-pending": "Awaiting Approval",
  rejected: "Rejected",
  accepted: "Confirmed",
  paid: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const getBookingStatusLabel = (status) =>
  BOOKING_STATUS_LABELS[status] ||
  (status ? status.replace(/[-_]/g, " ") : "");

// A booking is cancellable only when nothing terminal has happened to it:
// not rejected, not already cancelled, not completed, and nobody has
// checked in yet (once someone is on the spot it's too late to cancel).
export const canCancelBooking = (item) => {
  if (!item) return false;
  const terminalStatuses = ["rejected", "cancelled", "completed"];
  if (terminalStatuses.includes(item.status)) return false;
  const log = item.log;
  if (log) {
    if (log.userCheckin && log.userCheckin.checkin) return false;
    if (log.hostCheckin && log.hostCheckin.checkin) return false;
    if (log.userCheckout && log.userCheckout.checkout) return false;
    if (log.hostCheckout && log.hostCheckout.checkout) return false;
  }
  return true;
};

const SPOT_STATUS_LABELS = {
  published: "Published",
  draft: "Draft",
};

export const getSpotStatusLabel = (status) =>
  SPOT_STATUS_LABELS[status] ||
  (status ? status.replace(/[-_]/g, " ") : "");

const PAYMENT_STATUS_LABELS = {
  succeeded: "Paid",
  pending: "Pending",
  failed: "Failed",
  refunded: "Refunded",
};

export const getPaymentStatusLabel = (status) =>
  PAYMENT_STATUS_LABELS[status] ||
  (status ? status.replace(/[-_]/g, " ") : "");

export const calculateCustomAmount = (
  ratePerHour,
  startDate,
  startTime,
  endDate,
  endTime,
) => {
  const rate = parseFloat(ratePerHour);
  if (isNaN(rate)) return 0;

  const startDateTime = moment(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  const endDateTime = moment(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm");

  if (!startDateTime.isValid() || !endDateTime.isValid()) return 0;

  const durationInHours = moment
    .duration(endDateTime.diff(startDateTime))
    .asHours();

  if (durationInHours < 0) return 0;

  return (durationInHours * rate).toFixed(2);
};

export const hasTimePassed = (timezone, startTime, bookingDate) => {
  const targetTime = momentTz.tz(
    `${bookingDate} ${startTime}`,
    ["YYYY-MM-DD hh:mm A", "YYYY-MM-DD HH:mm"],
    timezone,
  );
  const now = momentTz.tz(timezone);
  return now.isAfter(targetTime);
};

export const getTimeFrame = (startDate, startTime, endDate, endTime) => {
  const start = new Date(`${startDate}T${startTime}:00`);
  const end = new Date(`${endDate}T${endTime}:00`);
  const diffMs = end - start;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"}`;
  }
  if (diffMinutes < 1440) {
    const hours = parseFloat(diffMinutes / 60).toFixed(0);
    return `${hours} hour${hours === "1" ? "" : "s"}`;
  }
  const days = parseFloat(diffMinutes / 1440).toFixed(0);
  return `${days} day${days === "1" ? "" : "s"}`;
};

export const convertAvailabiltyArr = (arrA) => {
  const arrB = Object.values(
    arrA.reduce((acc, curr) => {
      if (!acc[curr.day]) {
        acc[curr.day] = {
          day: curr.day,
          hours: [],
        };
      }

      if (curr.startTime && curr.endTime) {
        const tempItem = {
          startTime: curr.startTime,
          endTime: curr.endTime,
        };

        if (curr.id) {
          tempItem.id = curr.id;
        }

        acc[curr.day].hours.push(tempItem);
      }

      return acc;
    }, {})
  );

  return arrB;
};

export const formateNumber = (amount) => {
  const rate = parseFloat(amount);
  return parseFloat(rate.toFixed(2));
};

// Safe display for table cells: returns an em-dash for null, undefined,
// empty strings, AND the stringified forms "null" / "undefined" that the
// backend occasionally serialises missing values as.
export const display = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  if (s === "" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined")
    return fallback;
  return s;
};

// "MM/DD/YY hh:mm A" formatted arrival from a booking/request item.
// Falls back to the raw date+time string if moment can't parse it, or the
// em-dash if both fields are missing. Handles either bookings (startDate /
// startTime) or time-change requests (oldStartDate / oldStartTime).
export const formatBookingArrival = (item, fallback = "—") => {
  if (!item) return fallback;
  const date = item.oldStartDate || item.startDate;
  const time = item.oldStartTime || item.startTime;
  if (!date || !time) return fallback;
  const m = moment(`${date} ${time}`, [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD hh:mm A",
  ]);
  return m.isValid() ? m.format("MM/DD/YY hh:mm A") : `${date} ${time}`;
};

// True if the booking's arrival window has already passed. Used to gate
// host actions on the Requests list — once the arrival time is in the
// past it's too late to accept/deny/pay.
export const isBookingArrivalPassed = (item) => {
  if (!item) return false;
  const date = item.oldStartDate || item.startDate;
  const time = item.oldStartTime || item.startTime;
  if (!date || !time) return false;
  const m = moment(`${date} ${time}`, [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD hh:mm A",
  ]);
  if (!m.isValid()) return false;
  return m.isBefore(moment());
};

export const getFilterDate = (selectedDate) => {
  const today = moment();
  const selected = moment(selectedDate);

  if (today.format("YYYY/MM/DD") === selected.format("YYYY/MM/DD")) {
    return "Today";
  } else {
    return selected.format("MM/DD/YYYY");
  }
};
